import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Modal, alert } from 'equasion-lib';
import { FiPrinter } from 'react-icons/fi';
import { useRootStore } from '@/lib/providers/RootProvider';
import {
  REGENERATE_ORDER_STICKERS,
  isStickerReprintStatus,
  stickerReprintOrderNotice
} from '@/lib/stickerReprint';
import './ReprintStickersButton.scss';

type ConfirmProps = {
  orderId: string;
  companyId: string;
  vendorSalesOrderNumber?: string | null;
  /** False: the stickers are only regenerated, for download. */
  rxPrintEnabled: boolean;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onClose: () => void;
  onDone?: () => void;
};

/**
 * The confirm body. It owns the mutation so the pages that show the button need no Apollo client
 * until the user actually asks for a reprint.
 */
const ReprintStickersConfirm = ({
  orderId,
  companyId,
  vendorSalesOrderNumber,
  rxPrintEnabled,
  busy,
  setBusy,
  onClose,
  onDone
}: ConfirmProps) => {
  const [regenerate] = useMutation(REGENERATE_ORDER_STICKERS);

  const handleReprint = async () => {
    setBusy(true);
    try {
      const { data } = await regenerate({ variables: { orderId, companyId } });
      const result = data?.salesOrderRegenerateStickers;
      if (result) {
        const notice = stickerReprintOrderNotice(result);
        alert(notice.title, notice.message, 6, notice.type);
      }
      onDone?.();
    } catch (e: any) {
      alert('Could not reprint stickers', e?.message || 'Try again.', 6, 'error');
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div className="reprint-stickers-modal__body">
      <strong>{rxPrintEnabled ? 'Regenerate & print stickers?' : 'Regenerate stickers?'}</strong>
      <p>
        {rxPrintEnabled
          ? `The stickers for SO #${vendorSalesOrderNumber} are regenerated with the current template and printed through RxPrint — at your shipping lane when you have one selected.`
          : `The stickers for SO #${vendorSalesOrderNumber} are regenerated with the current template. RxPrint is not on, so they will not print.`}
      </p>
      <div>
        <Button type="primary" fullWidth disabled={busy} {...{ onClick: handleReprint }}>
          {busy ? 'Sending…' : rxPrintEnabled ? 'Regenerate & Print' : 'Regenerate Stickers'}
        </Button>
        <Button type="secondary" fullWidth disabled={busy} {...{ onClick: onClose }}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * "Regenerate & Print Stickers": regenerate one order's product stickers with the current template and print
 * them through RxPrint (the user's shipping lane when set) — unlike the page's "Re-print Stickers", which
 * sends the stickers already filed. Renders nothing before Shipping. With `rxPrintEnabled` false
 * the button reads "Regenerate Stickers" — the stickers are still regenerated, but nothing prints.
 * The flag is required: guessing "on" would promise a print that never comes.
 */
const ReprintStickersButton = ({
  orderId,
  vendorSalesOrderNumber,
  vendorStatus,
  rxPrintEnabled,
  className,
  onDone
}: {
  orderId?: string | null;
  vendorSalesOrderNumber?: string | null;
  vendorStatus?: string | null;
  rxPrintEnabled: boolean;
  /** Added to the button's own class, e.g. to line it up with the page's document buttons. */
  className?: string;
  /** After the stickers were queued — e.g. refetch the order's documents. */
  onDone?: () => void;
}) => {
  const selectedCompany = useRootStore((state) => state.selectedCompany) as any;
  const companyId = selectedCompany?.id;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!orderId || !companyId || !isStickerReprintStatus(vendorStatus)) return null;

  return (
    <>
      <Button
        type="secondary"
        fullWidth
        className={['reprint-stickers-button', className].filter(Boolean).join(' ')}
        iconBefore={<FiPrinter />}
        disabled={busy}
        {...{ onClick: () => setConfirmOpen(true) }}
      >
        {rxPrintEnabled ? 'Regenerate & Print Stickers' : 'Regenerate Stickers'}
      </Button>
      <Modal
        className="reprint-stickers-modal"
        modalOpen={confirmOpen}
        setModalOpen={(open) => {
          if (!open && !busy) setConfirmOpen(false);
        }}
      >
        {confirmOpen && (
          <ReprintStickersConfirm
            orderId={orderId}
            companyId={companyId}
            vendorSalesOrderNumber={vendorSalesOrderNumber}
            rxPrintEnabled={rxPrintEnabled}
            busy={busy}
            setBusy={setBusy}
            onClose={() => setConfirmOpen(false)}
            onDone={onDone}
          />
        )}
      </Modal>
    </>
  );
};

export default ReprintStickersButton;
